import update from 'immutability-helper';
import { clamp, cloneDeep, findIndex } from 'lodash-es';
import { serializer } from '@marvelapp/design-creator';
import { divideValues, getElements } from '@marvelapp/design-to-code/lib/utils';
import * as actionTypes from '../constants/actionTypes';
import * as colorMode from '../constants/colorModes';
import * as units from '../constants/measurementUnits';
import * as requestStatus from '../constants/requestStatus';
import { DESIGN_TOOL } from '../constants/sourcePlatform';
import * as zoom from '../constants/zoom';
import { getSelectedElementIndex } from '../selectors/handoff';
import * as tree from '../utils/handoff/tree';
export var initialState = {
    isOpen: false,
    selectedItemIndex: null,
    hoveredItemIndex: null,
    zoomPosition: zoom.DEFAULT_POSITION,
    zoomFit: true,
    initialZoomSet: false,
    canvas: null,
    flatCanvas: null,
    unit: units.PIXEL,
    requestStatus: requestStatus.IDLE,
    percentageBasedGuidelines: false,
    colorMode: colorMode.HEX,
};
export default function handoffReducer(state, action) {
    if (state === void 0) { state = initialState; }
    switch (action.type) {
        case actionTypes.SHOW_SCREEN:
            return update(state, {
                canvas: { $set: null },
                flatCanvas: { $set: null },
                selectedItemIndex: { $set: null },
                hoveredItemIndex: { $set: null },
                requestStatus: { $set: requestStatus.IDLE },
            });
        case actionTypes.LOAD_CANVAS_STARTING:
            return update(state, { requestStatus: { $set: requestStatus.PENDING } });
        case actionTypes.LOAD_CANVAS_DONE:
            return setCanvas(state, action.canvas, action.sourcePlatform);
        case actionTypes.LOAD_CANVAS_FAILED:
            return update(state, { requestStatus: { $set: requestStatus.ERROR } });
        case actionTypes.SHOW_HANDOFF:
            return enterHandoffMode(state);
        case actionTypes.HIDE_HANDOFF:
            return leaveHandoffMode(state);
        case actionTypes.CHANGE_HANDOFF_SELECTED_ITEM: {
            var newState = updateSelectedElement(state, action.index);
            newState = updateHoveredElement(newState, action.index);
            return newState;
        }
        case actionTypes.CHANGE_HANDOFF_SELECTED_ITEM_BY_UUID: {
            return updateSelectedElementByUUID(state, action.uuid);
        }
        case actionTypes.INCREMENT_ZOOM:
            return incrementZoom(state);
        case actionTypes.DECREMENT_ZOOM:
            return decrementZoom(state);
        case actionTypes.SET_INITIAL_HANDOFF_ZOOM_FLAG:
            return setInitialZoomFlag(state);
        case actionTypes.DESELECT_HANDOFF_ITEM:
            return deselectHandoffItem(state);
        case actionTypes.CHANGE_HANDOFF_HOVERED_ITEM:
            return updateHoveredElement(state, action.index);
        case actionTypes.CHANGE_HANDOFF_HOVERED_ITEM_BY_UUID: {
            return updateHoveredElementByUUID(state, action.uuid);
        }
        case actionTypes.SET_CANVAS:
            return setCanvas(state, action.canvas, action.sourcePlatform);
        case actionTypes.LOAD_FULL_PROJECT_DONE:
            return setInitalUnit(state, action.project.isWeb);
        case actionTypes.SET_UNIT:
            return update(state, {
                unit: { $set: action.unit },
            });
        case actionTypes.SET_COLOR_MODE:
            return update(state, {
                colorMode: { $set: action.colorMode },
            });
        case actionTypes.LOCATION_CHANGED:
            if (action.inHandoff !== state.isOpen) {
                if (action.inHandoff) {
                    return enterHandoffMode(state);
                }
                return leaveHandoffMode(state);
            }
            return state;
        case actionTypes.UPDATE_ZOOM_POSITION:
            return updateZoomPosition(state, action.zoomPosition);
        case actionTypes.SET_PERCENTAGE_BASED_GUIDELINES:
            return update(state, {
                percentageBasedGuidelines: { $set: action.value },
            });
        default:
            return state;
    }
}
function updateZoomPosition(state, zoomPosition, zoomFit) {
    if (zoomFit === void 0) { zoomFit = true; }
    return update(state, {
        zoomPosition: { $set: zoomPosition },
        zoomFit: { $set: zoomFit },
    });
}
function enterHandoffMode(state) {
    return update(state, {
        isOpen: { $set: true },
    });
}
function leaveHandoffMode(state) {
    return update(state, { isOpen: { $set: false } });
}
function setInitialZoomFlag(state) {
    return update(state, { initialZoomSet: { $set: true } });
}
function incrementZoom(state) {
    return updateZoomPosition(state, clampZoom(state.zoomPosition + 1), false);
}
function decrementZoom(state) {
    return updateZoomPosition(state, clampZoom(state.zoomPosition - 1), false);
}
function updateSelectedElement(state, index) {
    var actualIndexToSelect = getSelectedElementIndex({ handoff: state }, index);
    return update(state, { selectedItemIndex: { $set: actualIndexToSelect } });
}
function deselectHandoffItem(state) {
    return update(state, { selectedItemIndex: { $set: null } });
}
function updateHoveredElement(state, index) {
    if (index !== null) {
        var selected = state.flatCanvas[state.selectedItemIndex];
        var element = state.flatCanvas[index];
        if (tree.isElementNested(element)) {
            var hovered = tree.getAncestorOfElementThatIsChildOfCurrentSelection(selected, element);
            if (!hovered) {
                hovered = tree.getAncestorOfElementThatIsSiblingOfCurrentSelection(selected, element);
            }
            if (!hovered) {
                hovered = tree.getAncestorOfElementThatIsParentOfCurrentSelection(selected, element);
            }
            if (!hovered) {
                if (tree.isElementInSameTopLevelGroupAsCurrentSelection(selected, element)) {
                    hovered = element;
                }
            }
            if (!hovered) {
                hovered = tree.getTopLevelGroup(element, selected);
            }
            /* eslint-disable no-param-reassign */
            index = getElementIndex(state, hovered);
        }
    }
    return update(state, { hoveredItemIndex: { $set: index } });
}
function updateSelectedElementByUUID(state, uuid) {
    var newIndex = getItemIndexByUUID(state, uuid);
    if (newIndex > -1) {
        var newState = update(state, { selectedItemIndex: { $set: newIndex } });
        newState = updateHoveredElement(newState, newIndex);
        return newState;
    }
    return state;
}
function updateHoveredElementByUUID(state, uuid) {
    if (!uuid) {
        return updateHoveredElement(state, null);
    }
    var newIndex = getItemIndexByUUID(state, uuid);
    if (newIndex > -1) {
        return updateHoveredElement(state, newIndex);
    }
    return state;
}
function clampZoom(value) {
    return clamp(value, zoom.MIN_POSITION, zoom.MAX_POSITION);
}
function setCanvas(state, canvas, sourcePlatform) {
    var cloned = __cloneCanvasWithUUID(canvas);
    var flatCanvas;
    try {
        flatCanvas = getElements(cloned, sourcePlatform === DESIGN_TOOL).filter(
        // don't render the children of the compound paths as they provide no useful information for handoff
        function (element) { return !tree.isElementDescendantOfType(element, 'CompoundPath'); });
    }
    catch (e) {
        return update(state, {
            requestStatus: { $set: requestStatus.ERROR },
        });
    }
    // getElements takes care of designRatio for the elements so here we
    // need to handle for the artboard
    var _a = canvas.artboard, _b = _a.designRatio, designRatio = _b === void 0 ? 1 : _b, _c = _a.pixelRatio, pixelRatio = _c === void 0 ? 1 : _c;
    cloned.artboard.size = divideValues(cloned.artboard.size, designRatio);
    cloned.artboard.pixelRatio = pixelRatio * designRatio;
    return update(state, {
        canvas: { $set: cloned },
        flatCanvas: { $set: flatCanvas },
        requestStatus: { $set: requestStatus.SUCCESS },
    });
}
function getElementIndex(state, element) {
    return findIndex(state.flatCanvas, function (el) { return el === element; });
}
function setInitalUnit(state, isWeb) {
    var unit = isWeb ? units.PIXEL : units.POINT;
    return update(state, {
        unit: { $set: unit },
    });
}
function __cloneCanvasWithUUID(canvas) {
    // This function is a HACK!
    // Interactions with prototype viewer rely on an id set by design-to-code when creating
    // a flat structure of the easel data here, and are based on an array index.
    // https://github.com/marvelapp/design-to-code/blob/master/src/utils.js#L37
    //
    // The new layers panel in handoff requires a normalized version of the easel data,
    // however this cannot reliably be created in this project because the above function
    // overwrites item ids and breaks the hirarchy of items when the tree is falttened.
    //
    // This function insted creates a clone of the canvas tree and patches the children with
    // __unsafeUUID, so that when the canvas is normalized for the use in layers
    // panel, we can reference __unsafeUUID with the id in the flat canvas. We say
    // this is "unsafe" because adding this directly to the canvas tree means
    // we are breaking the single source of truth of what a canvas tree should look like!
    var cloned = cloneDeep(canvas);
    var assignUuidToChildren = function (children) {
        children.forEach(function (child) {
            child.__unsafeUUID = child.id;
            if (child.children) {
                assignUuidToChildren(child.children);
            }
        });
    };
    var normalized = serializer.normalizeCanvas(cloned);
    var doubleParsed = serializer.denormalizeCanvas(normalized);
    assignUuidToChildren(doubleParsed.children);
    return doubleParsed;
}
function getItemIndexByUUID(state, uuid) {
    var newIndex = state.flatCanvas.findIndex(function (item) { return item.__unsafeUUID === uuid; });
    return newIndex;
}
